import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';
import { getCurrencySymbol } from '../utils/salaryConversion';
import { SalaryDistributionChart } from './SalaryDistributionChart';
import type { DisplayJob } from '../../../../../types';

export interface CompensationSectionProps {
  displaySalary: {
    min: number;
    max: number;
    currency: string;
    period: 'annual' | 'monthly' | 'daily' | 'hourly';
  };
  compensationExpanded: boolean;
  onToggleExpanded: () => void;
  showOriginalSalary: boolean;
  shouldShowOriginalButton: boolean;
  onToggleShowOriginal: () => void;
  salaryDetails?: string | null;
  vacationWeeks?: number | null;
  displayJob?: DisplayJob | null;
  salaryRangeMin?: number | null;
  salaryRangeMax?: number | null;
  currency?: string | null;
  salaryPeriod?: string | null;
  jobLocale: string;
  location?: string | string[];
  normalisedTitle?: string | null;
  title?: string | null;
  normalisedRoleLevel?: string | null;
  onViewBenefits?: () => void;
  /** Key to force salary chart remount so it paints in visible container (island). */
  chartRemountKey?: number;
}

export const CompensationSection: React.FC<CompensationSectionProps> = ({
  displaySalary,
  compensationExpanded,
  onToggleExpanded,
  showOriginalSalary,
  shouldShowOriginalButton,
  onToggleShowOriginal,
  salaryDetails,
  vacationWeeks,
  displayJob,
  salaryRangeMin,
  salaryRangeMax,
  currency,
  salaryPeriod,
  jobLocale,
  location,
  normalisedTitle,
  title,
  normalisedRoleLevel,
  onViewBenefits,
  chartRemountKey = 0,
}) => {
  const intl = useIntl();

  if (!displaySalary) return null;

  const periodLabel = displaySalary.period === 'annual'
    ? intl.formatMessage({ id: 'jobDetailPage.period.year', defaultMessage: 'year', description: 'Time period unit for salary (per year)' })
    : displaySalary.period === 'monthly'
    ? intl.formatMessage({ id: 'jobDetailPage.period.month', defaultMessage: 'month', description: 'Time period unit for salary (per month)' })
    : displaySalary.period === 'daily'
    ? intl.formatMessage({ id: 'jobDetailPage.period.day', defaultMessage: 'day', description: 'Time period unit for salary (per day)' })
    : intl.formatMessage({ id: 'jobDetailPage.period.hour', defaultMessage: 'hour', description: 'Time period unit for salary (per hour)' });

  const hasSalaryStats = displayJob?.allSalaryStats || displayJob?.companySalaryStats || displayJob?.salaryStats || displayJob?.locationSalaryStats;

  return (
    <div className="mb-6 p-5 rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-800 shadow overflow-hidden">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="text-xs font-semibold text-green-700 dark:text-green-300 uppercase tracking-wide">
            <FormattedMessage 
              id="jobDetailPage.compensation" 
              defaultMessage="Compensation" 
              description="Section label for job compensation information (salary, benefits, vacation)" 
            />
          </div>
          <button
            type="button"
            onClick={onToggleExpanded}
            className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-transform cursor-pointer"
            aria-label={compensationExpanded 
              ? intl.formatMessage({ id: 'jobDetailPage.collapseCompensation', defaultMessage: 'Collapse compensation details', description: 'Button label to collapse/hide detailed compensation information' })
              : intl.formatMessage({ id: 'jobDetailPage.expandCompensation', defaultMessage: 'Expand compensation details', description: 'Button label to expand/show detailed compensation information' })}
          >
            {compensationExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronUp className="h-4 w-4" />
            )}
          </button>
        </div>
        {shouldShowOriginalButton && (
          <button
            type="button"
            onClick={onToggleShowOriginal}
            className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 underline cursor-pointer"
          >
            {showOriginalSalary ? (
              <FormattedMessage 
                id="jobDetailPage.showConverted" 
                defaultMessage="Show EUR/month" 
                description="Button label to display salary converted to EUR per month" 
              />
            ) : (
              <FormattedMessage 
                id="jobDetailPage.seeOriginal" 
                defaultMessage="See original" 
                description="Button label to view original salary amount before conversion" 
              />
            )}
          </button>
        )}
      </div>
      <div className="text-3xl font-bold text-green-900 dark:text-green-100">
        {getCurrencySymbol(displaySalary.currency)}{displaySalary.min.toLocaleString()} - {getCurrencySymbol(displaySalary.currency)}{displaySalary.max.toLocaleString()}
        <span className="text-lg font-normal text-green-700 dark:text-green-300 ml-2">
          /{periodLabel}
        </span>
      </div>
      {compensationExpanded && (
        <>
          {salaryDetails && (
            <div className="text-sm text-green-700 dark:text-green-300 mt-2 italic">
              {fixMojibake(salaryDetails)}
            </div>
          )}
          {vacationWeeks != null && vacationWeeks > 0 && (
            <div className="text-xs text-green-600 dark:text-green-400 mt-2">
              <FormattedMessage 
                id="jobDetailPage.vacationWeeks" 
                defaultMessage="Vacation: {weeks} weeks"
                description="Label showing number of vacation weeks offered, where {weeks} is the number" 
                values={{ weeks: vacationWeeks }} 
              />
            </div>
          )}
          {displayJob?.benefits && displayJob.benefits.length > 0 && onViewBenefits && (
            <button
              onClick={onViewBenefits}
              className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 underline mt-2 cursor-pointer"
            >
              <FormattedMessage 
                id="jobDetailPage.viewBenefits" 
                defaultMessage="View benefits" 
                description="Button label to navigate to benefits section on job detail page" 
              />
            </button>
          )}
          {hasSalaryStats && (
            <SalaryDistributionChart
              key={chartRemountKey}
              salaryStats={displayJob?.companySalaryStats || displayJob?.salaryStats}
              locationSalaryStats={displayJob?.locationSalaryStats}
              allSalaryStats={displayJob?.allSalaryStats}
              jobSalaryMin={salaryRangeMin}
              jobSalaryMax={salaryRangeMax}
              currency={currency}
              salaryPeriod={salaryPeriod}
              locale={jobLocale}
              jobLocation={location}
              jobTitle={normalisedTitle || title}
              jobRoleLevel={normalisedRoleLevel}
            />
          )}
        </>
      )}
    </div>
  );
};
